/**
 * AIFlock Theme
 * Tema inspirado en el diseño de aiflock.ai
 *
 * @description Tema moderno con gradientes púrpura-magenta, diseño limpio
 * y estética profesional SaaS. Basado en la identidad visual de AIFlock.
 *
 * Paleta principal:
 * - Primary: Púrpura vibrante (#9333EA)
 * - Secondary: Magenta/Rosa (#EC4899)
 * - Gradient: Púrpura → Magenta
 * - Success: Verde esmeralda (#22C55E)
 * - Warning: Ámbar oscuro (#B45309)
 * - Cyan: Turquesa (#22D3EE)
 */

export const aiflockTheme = {
  name: 'AIFlock',
  mode: 'light',

  // Paleta de colores principal
  palette: {
    primary: {
      main: '#9333EA',           // Púrpura vibrante (headings)
      light: '#A855F7',          // Púrpura claro
      dark: '#7C3AED',           // Púrpura oscuro
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#EC4899',           // Magenta/Rosa
      light: '#F472B6',          // Rosa claro
      dark: '#DB2777',           // Rosa oscuro
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626'
    },
    warning: {
      main: '#B45309',           // Ámbar oscuro (WCAG AA 5.5:1)
      light: '#D97706',
      dark: '#92400E',
      contrastText: '#FFFFFF'
    },
    info: {
      main: '#22D3EE',           // Cyan/Turquesa
      light: '#67E8F9',
      dark: '#06B6D4'
    },
    success: {
      main: '#22C55E',           // Verde esmeralda (checkmarks)
      light: '#4ADE80',
      dark: '#16A34A'
    },

    // Fondos y superficies
    background: {
      default: '#FAFAFA',        // Fondo principal gris muy claro
      paper: '#FFFFFF',          // Tarjetas blancas
      elevated: '#F5F5F5'        // Elementos elevados
    },

    // Escala de grises (para componentes que usan grey.X)
    grey: {
      50: '#F9FAFB',             // Gris muy claro (suggestion cards)
      100: '#F3F4F6',            // Gris claro (hover state)
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    },

    // Textos
    text: {
      primary: '#1F2937',        // Gris oscuro para cuerpo
      secondary: '#6B7280',      // Gris medio
      disabled: '#9CA3AF',
      hint: '#D1D5DB'
    },

    // Colores de acción
    action: {
      active: '#9333EA',
      hover: 'rgba(147, 51, 234, 0.04)',
      selected: 'rgba(147, 51, 234, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)'
    },

    // Divisores
    divider: 'rgba(0, 0, 0, 0.06)',

    // Colores de marca (mantiene compatibilidad)
    brand: {
      main: '#9333EA',           // Púrpura AIFlock
      light: '#A855F7',
      dark: '#7C3AED',
      contrastText: '#FFFFFF'
    },

    // Colores adicionales para gradientes
    gradient: {
      start: '#9333EA',          // Púrpura
      end: '#EC4899',            // Magenta
      direction: '135deg'
    }
  },

  // Efectos visuales
  effects: {
    shadows: {
      subtle: '0px 1px 4px rgba(0, 0, 0, 0.05)',
      medium: '0px 4px 12px rgba(0, 0, 0, 0.08)',
      strong: '0px 8px 24px rgba(0, 0, 0, 0.12)',
      brand: '0 4px 20px rgba(147, 51, 234, 0.15)',
      brandMedium: '0 2px 8px rgba(147, 51, 234, 0.10)',
      brandStrong: '0 4px 16px rgba(147, 51, 234, 0.20)'
    },

    hover: {
      background: 'rgba(0, 0, 0, 0.02)',
      primary: 'rgba(147, 51, 234, 0.06)',
      card: '0px 4px 12px rgba(0, 0, 0, 0.08)',
      cardBackground: 'rgba(147, 51, 234, 0.02)'
    },

    overlay: {
      loading: 'rgba(0, 0, 0, 0.6)',
      modal: 'rgba(0, 0, 0, 0.4)',
      subtle: 'rgba(0, 0, 0, 0.2)'
    },

    warning: {
      background: 'rgba(249, 115, 22, 0.1)',
      hover: 'rgba(249, 115, 22, 0.15)',
      border: 'rgba(249, 115, 22, 0.3)'
    },

    // Gradientes característicos de AIFlock
    gradients: {
      primary: 'linear-gradient(135deg, #9333EA 0%, #EC4899 100%)',
      primaryVertical: 'linear-gradient(180deg, #9333EA 0%, #EC4899 100%)',
      primaryHorizontal: 'linear-gradient(90deg, #9333EA 0%, #EC4899 100%)',
      subtle: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
      card: 'linear-gradient(135deg, #9333EA 0%, #D946EF 50%, #EC4899 100%)'
    }
  },

  // Configuración por sección
  sections: {
    // Chat Interface
    chat: {
      background: '#FAFAFA',
      messageBg: '#FFFFFF',
      inputBg: '#FFFFFF',
      userMessageBg: '#F3EAFC',  // Solid purple tint (no transparency overlap issues)
      botMessageBg: '#FFFFFF',
      border: '#E5E7EB',
      shadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
    },

    // Flow Editor
    editor: {
      // Neutro templado, no gris puro: #FAFAFA tiene R=G=B, y rodeado del violeta de la
      // sidebar el ojo lo desplaza hacia el complementario (se percibe verdoso).
      // b*=1,66 y ΔE=1,70 respecto al #FAFAFA de siempre: por encima del umbral de
      // percepción — un #FBFAF9 (ΔE=0,62) habría sido un placebo. Es el ÚNICO fondo de
      // la app que se templa: `palette.background.default`, `chat.background` y
      // `roles.sidebarBg` siguen en #FAFAFA, y el canvas gana así separación con ellos.
      canvasBg: '#FBF9F6',
      nodeBg: '#FFFFFF',
      nodeBorder: '#E5E7EB',
      nodeSelected: '#9333EA',
      nodeHover: '#F9FAFB',
      toolbarBg: 'rgba(255, 255, 255, 0.95)',
      toolbarShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
      connectionDefault: '#D1D5DB',
      connectionActive: '#9333EA',
      miniMapBg: 'rgba(250, 250, 250, 0.9)',
      // ~1,33:1 sobre canvasBg: textura de papel, no cuadrícula. Cálido para acompañar
      // al fondo (el #F3F4F6 anterior era frío y, además, nunca llegó a usarse).
      gridColor: '#DEDAD5'
    },

    // Roles Editor
    roles: {
      cardBg: '#FFFFFF',
      sidebarBg: '#FAFAFA',
      accent: '#9333EA',
      border: '#E5E7EB',
      hover: '#F9FAFB',
      selected: 'rgba(147, 51, 234, 0.08)'
    },

    // Settings
    settings: {
      panelBg: '#FAFAFA',
      cardBg: '#FFFFFF',
      border: '#E5E7EB',
      accent: '#9333EA'
    },

    // Onboarding
    onboarding: {
      bg: '#FAFAFA',
      cardBg: '#FFFFFF',
      accent: '#9333EA',
      progress: '#A855F7'
    }
  },

  // Tipografía
  typography: {
    fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#9333EA',        // Headings en púrpura
      lineHeight: 1.2,
      fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#9333EA',
      lineHeight: 1.3,
      fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#9333EA',
      lineHeight: 1.4,
      fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#1F2937',
      lineHeight: 1.4
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#1F2937',
      lineHeight: 1.5
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#374151',
      lineHeight: 1.5
    },

    body1: { fontSize: '1rem', color: '#374151', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', color: '#6B7280', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', color: '#9CA3AF', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' }
  },

  // Sombras
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 4px 8px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 20px rgba(0, 0, 0, 0.08)',
    xl: '0 20px 40px rgba(0, 0, 0, 0.1)',

    card: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
    modal: '0 25px 50px rgba(0, 0, 0, 0.12)',
    tooltip: '0 4px 6px rgba(0, 0, 0, 0.08)'
  },

  // Espaciado
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },

  // Animaciones
  animations: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',

    fadeIn: 'fadeIn 200ms ease-out',
    slideUp: 'slideUp 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'bounce 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },

  // Breakpoints
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1280,
    xl: 1920
  },

  // Overrides de componentes MUI
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#FAFAFA !important',
          color: '#374151',
          fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        html: {
          backgroundColor: '#FAFAFA !important'
        },
        '#root': {
          backgroundColor: '#FAFAFA !important',
          minHeight: '100dvh'
        }
      }
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          // Don't hardcode backgroundColor - let components use sx prop or default to theme.palette.background.paper
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          borderRadius: 12
        }
      }
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(147, 51, 234, 0.15)'
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(229, 231, 235, 0.6)',
            color: 'rgba(107, 114, 128, 0.8)'
          }
        },
        contained: {
          '&.Mui-disabled': {
            backgroundColor: 'rgba(229, 231, 235, 0.6)',
            color: 'rgba(107, 114, 128, 0.7)',
            backgroundImage: 'none'
          }
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #9333EA 0%, #A855F7 100%)',
          color: '#FFFFFF',
          '&:hover': {
            background: 'linear-gradient(135deg, #7C3AED 0%, #9333EA 100%)'
          }
        },
        outlined: {
          borderColor: '#9333EA',
          color: '#9333EA',
          '&:hover': {
            backgroundColor: 'rgba(147, 51, 234, 0.04)',
            borderColor: '#7C3AED'
          }
        }
      }
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
        }
      }
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500
        },
        filled: {
          '&.MuiChip-colorWarning': {
            background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
            color: '#FFFFFF'
          }
        },
        outlined: {
          borderColor: '#E5E7EB'
        }
      }
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#9333EA',
              borderWidth: 2
            }
          }
        }
      }
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: 'rgba(147, 51, 234, 0.1)'
        },
        bar: {
          borderRadius: 4,
          background: 'linear-gradient(90deg, #9333EA 0%, #EC4899 100%)'
        }
      }
    },

    // Note: MuiAvatar NOT overridden globally to preserve role-specific colors
    // Role colors come from database or are auto-generated per role
  }
};

/**
 * AIFlock Dark Theme
 * Versión oscura del tema AIFlock
 */
export const aiflockDarkTheme = {
  name: 'AIFlock Dark',
  mode: 'dark',

  palette: {
    primary: {
      main: '#A855F7',           // Púrpura más brillante para dark mode
      light: '#C084FC',
      dark: '#9333EA',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#F472B6',           // Rosa más brillante
      light: '#F9A8D4',
      dark: '#EC4899',
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#F87171',
      light: '#FCA5A5',
      dark: '#EF4444'
    },
    warning: {
      main: '#D97706',           // Ámbar medio para dark mode
      light: '#F59E0B',
      dark: '#B45309',
      contrastText: '#FFFFFF'
    },
    info: {
      main: '#67E8F9',
      light: '#A5F3FC',
      dark: '#22D3EE'
    },
    success: {
      main: '#4ADE80',
      light: '#86EFAC',
      dark: '#22C55E'
    },

    background: {
      default: '#0F0F0F',        // Negro profundo
      paper: '#1A1A1A',          // Gris muy oscuro
      elevated: '#242424'
    },

    // Escala de grises para dark mode (invertida para fondos oscuros)
    grey: {
      50: '#1A1A1A',             // Oscuro (suggestion cards bg)
      100: '#242424',            // Oscuro claro (hover state)
      200: '#2D2D2D',
      300: '#3D3D3D',
      400: '#525252',
      500: '#6B7280',
      600: '#9CA3AF',
      700: '#D1D5DB',
      800: '#E5E7EB',
      900: '#F3F4F6'
    },

    text: {
      primary: '#F9FAFB',
      secondary: '#D1D5DB',
      disabled: '#6B7280',
      hint: '#4B5563'
    },

    action: {
      active: '#A855F7',
      hover: 'rgba(168, 85, 247, 0.08)',
      selected: 'rgba(168, 85, 247, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.26)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)'
    },

    divider: 'rgba(255, 255, 255, 0.08)',

    brand: {
      main: '#A855F7',
      light: '#C084FC',
      dark: '#9333EA',
      contrastText: '#FFFFFF'
    },

    gradient: {
      start: '#A855F7',
      end: '#F472B6',
      direction: '135deg'
    }
  },

  effects: {
    shadows: {
      subtle: '0px 1px 4px rgba(0, 0, 0, 0.3)',
      medium: '0px 4px 12px rgba(0, 0, 0, 0.4)',
      strong: '0px 8px 24px rgba(0, 0, 0, 0.5)',
      brand: '0 4px 20px rgba(168, 85, 247, 0.25)',
      brandMedium: '0 2px 8px rgba(168, 85, 247, 0.15)',
      brandStrong: '0 4px 16px rgba(168, 85, 247, 0.30)'
    },

    hover: {
      background: 'rgba(255, 255, 255, 0.04)',
      primary: 'rgba(168, 85, 247, 0.12)',
      card: '0px 4px 12px rgba(0, 0, 0, 0.4)',
      cardBackground: 'rgba(168, 85, 247, 0.06)'
    },

    overlay: {
      loading: 'rgba(0, 0, 0, 0.8)',
      modal: 'rgba(0, 0, 0, 0.6)',
      subtle: 'rgba(0, 0, 0, 0.4)'
    },

    warning: {
      background: 'rgba(251, 146, 60, 0.15)',
      hover: 'rgba(251, 146, 60, 0.2)',
      border: 'rgba(251, 146, 60, 0.3)'
    },

    gradients: {
      primary: 'linear-gradient(135deg, #A855F7 0%, #F472B6 100%)',
      primaryVertical: 'linear-gradient(180deg, #A855F7 0%, #F472B6 100%)',
      primaryHorizontal: 'linear-gradient(90deg, #A855F7 0%, #F472B6 100%)',
      subtle: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(244, 114, 182, 0.15) 100%)',
      card: 'linear-gradient(135deg, #A855F7 0%, #D946EF 50%, #F472B6 100%)'
    }
  },

  sections: {
    chat: {
      background: '#0F0F0F',
      messageBg: '#1A1A1A',
      inputBg: '#1A1A1A',
      userMessageBg: '#2D1F3D',  // Solid purple tint for dark mode
      botMessageBg: '#242424',
      border: '#2D2D2D',
      shadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
    },

    editor: {
      canvasBg: '#0F0F0F',
      nodeBg: '#1A1A1A',
      nodeBorder: '#2D2D2D',
      nodeSelected: '#A855F7',
      nodeHover: '#242424',
      toolbarBg: 'rgba(26, 26, 26, 0.95)',
      toolbarShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      connectionDefault: '#4B5563',
      connectionActive: '#A855F7',
      miniMapBg: 'rgba(26, 26, 26, 0.9)',
      // Sube de #1F1F1F (~1,16:1, invisible en la práctica) a ~1,23:1 sobre canvasBg.
      // Hasta ahora el canvas oscuro pintaba la trama con el #91919a de React Flow, que
      // en dark destacaba MÁS que en claro; al conectar el token había que evitar el
      // extremo contrario, que era dejar el canvas liso del todo.
      gridColor: '#242424'
    },

    roles: {
      cardBg: '#1A1A1A',
      sidebarBg: '#0F0F0F',
      accent: '#A855F7',
      border: '#2D2D2D',
      hover: '#242424',
      selected: 'rgba(168, 85, 247, 0.15)'
    },

    settings: {
      panelBg: '#0F0F0F',
      cardBg: '#1A1A1A',
      border: '#2D2D2D',
      accent: '#A855F7'
    },

    onboarding: {
      bg: '#0F0F0F',
      cardBg: '#1A1A1A',
      accent: '#A855F7',
      progress: '#C084FC'
    }
  },

  typography: {
    fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#A855F7',
      lineHeight: 1.2,
      fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#A855F7',
      lineHeight: 1.3,
      fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#A855F7',
      lineHeight: 1.4,
      fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#F9FAFB',
      lineHeight: 1.4
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#F9FAFB',
      lineHeight: 1.5
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#E5E7EB',
      lineHeight: 1.5
    },

    body1: { fontSize: '1rem', color: '#E5E7EB', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', color: '#D1D5DB', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', color: '#9CA3AF', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' }
  },

  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    md: '0 4px 8px rgba(0, 0, 0, 0.4)',
    lg: '0 10px 20px rgba(0, 0, 0, 0.5)',
    xl: '0 20px 40px rgba(0, 0, 0, 0.6)',

    card: '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.4)',
    modal: '0 25px 50px rgba(0, 0, 0, 0.6)',
    tooltip: '0 4px 6px rgba(0, 0, 0, 0.4)'
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },

  animations: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',

    fadeIn: 'fadeIn 200ms ease-out',
    slideUp: 'slideUp 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'bounce 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },

  breakpoints: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1280,
    xl: 1920
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0F0F0F !important',
          color: '#E5E7EB',
          fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        html: {
          backgroundColor: '#0F0F0F !important'
        },
        '#root': {
          backgroundColor: '#0F0F0F !important',
          minHeight: '100dvh'
        }
      }
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          // Don't hardcode backgroundColor - let components use sx prop or default to theme.palette.background.paper
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          borderRadius: 12
        }
      }
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(168, 85, 247, 0.25)'
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(45, 45, 45, 0.6)',
            color: 'rgba(156, 163, 175, 0.6)'
          }
        },
        contained: {
          '&.Mui-disabled': {
            backgroundColor: 'rgba(45, 45, 45, 0.6)',
            color: 'rgba(156, 163, 175, 0.5)',
            backgroundImage: 'none'
          }
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #A855F7 0%, #C084FC 100%)',
          color: '#FFFFFF',
          '&:hover': {
            background: 'linear-gradient(135deg, #9333EA 0%, #A855F7 100%)'
          }
        },
        outlined: {
          borderColor: '#A855F7',
          color: '#A855F7',
          '&:hover': {
            backgroundColor: 'rgba(168, 85, 247, 0.08)',
            borderColor: '#C084FC'
          }
        }
      }
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #2D2D2D',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
        }
      }
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500
        },
        filled: {
          '&.MuiChip-colorWarning': {
            background: 'linear-gradient(135deg, #FB923C 0%, #FDBA74 100%)',
            color: '#1A1A1A'
          }
        },
        outlined: {
          borderColor: '#2D2D2D'
        }
      }
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#A855F7',
              borderWidth: 2
            }
          }
        }
      }
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: 'rgba(168, 85, 247, 0.15)'
        },
        bar: {
          borderRadius: 4,
          background: 'linear-gradient(90deg, #A855F7 0%, #F472B6 100%)'
        }
      }
    },

    // Note: MuiAvatar NOT overridden globally to preserve role-specific colors
    // Role colors come from database or are auto-generated per role
  }
};
